/**
 * Breadcrumb - Core Component (Passthrough Wrapper)
 *
 * This is a thin wrapper around shadcn/ui Breadcrumb components.
 * It provides a consistent import path and allows for future project-specific
 * defaults or modifications without changing consumer code.
 *
 * Purpose:
 * - Consistent import path: always import from '@/app/_components/core'
 * - Type re-exports for convenience
 * - Future-proof: modifications can be added here without breaking changes
 *
 * This component does NOT add custom styling or behavior - it passes through
 * all props directly to the underlying shadcn/ui components.
 *
 * @see {@link file://./../ui/breadcrumb.tsx} for the underlying implementation
 */
import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import {
    Breadcrumb as ShadcnBreadcrumb,
    BreadcrumbList as ShadcnBreadcrumbList,
    BreadcrumbItem as ShadcnBreadcrumbItem,
    BreadcrumbLink as ShadcnBreadcrumbLink,
    BreadcrumbPage as ShadcnBreadcrumbPage,
    BreadcrumbSeparator as ShadcnBreadcrumbSeparator,
    BreadcrumbEllipsis as ShadcnBreadcrumbEllipsis,
} from '@/app/_components/ui/breadcrumb';
import { cn } from '@/app/_lib/utils';

export interface BreadcrumbProps extends ComponentProps<'nav'> {
    /**
     * Custom className for styling
     */
    className?: string;
}

export interface BreadcrumbListProps extends ComponentProps<'ol'> {
    /**
     * Custom className for styling
     */
    className?: string;
}

export interface BreadcrumbItemProps extends ComponentProps<'li'> {
    /**
     * Custom className for styling
     */
    className?: string;
}

export interface BreadcrumbLinkProps extends ComponentProps<'a'> {
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Use as child component
     */
    asChild?: boolean;
}

export interface BreadcrumbPageProps extends ComponentProps<'span'> {
    /**
     * Custom className for styling
     */
    className?: string;
}

export interface BreadcrumbSeparatorProps extends ComponentProps<'li'> {
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Custom separator content
     */
    children?: ReactNode;
}

export interface BreadcrumbEllipsisProps extends ComponentProps<'span'> {
    /**
     * Custom className for styling
     */
    className?: string;
}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
    ({ className, ...props }, ref) => {
        return <ShadcnBreadcrumb ref={ref} className={cn(className)} {...props} />;
    }
);

export const BreadcrumbList = forwardRef<HTMLOListElement, BreadcrumbListProps>(
    ({ className, ...props }, ref) => {
        return <ShadcnBreadcrumbList ref={ref} className={cn(className)} {...props} />;
    }
);

export const BreadcrumbItem = forwardRef<HTMLLIElement, BreadcrumbItemProps>(
    ({ className, ...props }, ref) => {
        return <ShadcnBreadcrumbItem ref={ref} className={cn(className)} {...props} />;
    }
);

export const BreadcrumbLink = forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
    ({ className, asChild, ...props }, ref) => {
        return (
            <ShadcnBreadcrumbLink
                ref={ref}
                asChild={asChild}
                className={cn(className)}
                {...props}
            />
        );
    }
);

export const BreadcrumbPage = forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
    ({ className, ...props }, ref) => {
        return <ShadcnBreadcrumbPage ref={ref} className={cn(className)} {...props} />;
    }
);

export const BreadcrumbSeparator = forwardRef<HTMLLIElement, BreadcrumbSeparatorProps>(
    ({ className, children, ...props }, ref) => {
        return (
            <ShadcnBreadcrumbSeparator ref={ref} className={cn(className)} {...props}>
                {children}
            </ShadcnBreadcrumbSeparator>
        );
    }
);

export const BreadcrumbEllipsis = forwardRef<HTMLSpanElement, BreadcrumbEllipsisProps>(
    ({ className, ...props }, ref) => {
        return <ShadcnBreadcrumbEllipsis ref={ref} className={cn(className)} {...props} />;
    }
);

Breadcrumb.displayName = 'Breadcrumb';
BreadcrumbList.displayName = 'BreadcrumbList';
BreadcrumbItem.displayName = 'BreadcrumbItem';
BreadcrumbLink.displayName = 'BreadcrumbLink';
BreadcrumbPage.displayName = 'BreadcrumbPage';
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';
BreadcrumbEllipsis.displayName = 'BreadcrumbEllipsis';

export default Breadcrumb;
